import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { competitionInputSchema, type CompetitionInput } from '@pirouette/shared';
import { useCreateCompetition } from '@/data/competitions';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/competitions/new')({
  component: NewCompetition,
});

function NewCompetition() {
  const navigate = useNavigate();
  const createComp = useCreateCompetition();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CompetitionInput>({
    resolver: zodResolver(competitionInputSchema),
    mode: 'onBlur',
    defaultValues: { season: 'Spring 2027' },
  });

  async function onSubmit(values: CompetitionInput) {
    try {
      const created = await createComp.mutateAsync(values);
      toast({ title: 'Competition created', description: created.name, variant: 'success' });
      await navigate({ to: '/admin/competitions/$id', params: { id: created.id } });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <header>
        <Link
          to="/admin/competitions"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Competitions
        </Link>
        <h1 className="mt-2">New competition</h1>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        noValidate
        className="surface-elevated grid gap-5 p-6"
      >
        <div>
          <Label htmlFor="c-name">Name</Label>
          <Input
            id="c-name"
            placeholder="StarPower Regional"
            aria-invalid={!!errors.name}
            {...register('name')}
          />
          {errors.name && (
            <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
              {errors.name.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="c-org">Organizer</Label>
          <Input id="c-org" {...register('organizer')} placeholder="(optional)" />
        </div>
        <div>
          <Label htmlFor="c-venue">Venue</Label>
          <Input id="c-venue" {...register('venue_name')} placeholder="(optional)" />
        </div>
        <div>
          <Label htmlFor="c-season">Season</Label>
          <Input id="c-season" {...register('season')} />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <Label htmlFor="c-start">Start date</Label>
            <Input
              id="c-start"
              type="date"
              {...register('start_date')}
              aria-invalid={!!errors.start_date}
            />
            {errors.start_date && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.start_date.message}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="c-end">End date</Label>
            <Input
              id="c-end"
              type="date"
              {...register('end_date')}
              aria-invalid={!!errors.end_date}
            />
            {errors.end_date && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.end_date.message}
              </p>
            )}
          </div>
        </div>
        <div>
          <Label htmlFor="c-notes">Notes</Label>
          <textarea
            id="c-notes"
            rows={3}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 focus-visible:outline-none"
            {...register('notes')}
          />
        </div>
        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Create competition'}
          </Button>
          <Link to="/admin/competitions">
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
